/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import Backbone from 'backbone';
import _ from 'lodash';

import { getUserDetails, userHasRole } from '@/util/RoleUtils';
import OrderTypes from '@/enums/OrderTypes';
import { formatDisplayDate } from '@/util/MomentUtils';

import GetAllLUOrderLineItems from '../services/GetAllLUOrderLineItems';
import GetOFMFDRelocationByOrderId from '../services/GetOFMFDRelocationByOrderId';
import GetDeliveryScheduleInfoForMFDRelocationForm from '../services/GetDeliveryScheduleInfoForMFDRelocationForm';
import GetCustomerOrderDetails from '../services/GetCustomerOrderDetails';
import GetCustomerOrderById from '../services/GetCustomerOrderById';
import GetInternalPOCsByOrderId from '../services/GetInternalPOCsByOrderId';
import GetAllOrderStatus from '../../common/services/GetAllOrderStatus';

export default async function OrdersPrintCollectionLoader(orders) {
  const orderPrintViewCollection = new Backbone.Collection();
  await Promise.all(
    _.map(orders.models, async (order) => {
      const orderId = order.id;
      const [allLineItems, orderDetails, extOrderDetails, internalPOCs, statuses] = await Promise.all([
        GetAllLUOrderLineItems(),
        GetCustomerOrderById(orderId),
        GetCustomerOrderDetails(orderId),
        GetInternalPOCsByOrderId(orderId),
        GetAllOrderStatus()
      ]);

      const orderFormTypeId = orderDetails.orderFormTypeId;
      orderDetails.orderTypeId = orderFormTypeId;

      const isRelocation = orderFormTypeId === OrderTypes.RELOCATION ? true : false;
      const isCRSOwner = getUserDetails().id === orderDetails?.crsOwner;
      const isEPCOwner = getUserDetails().id === orderDetails?.epcOwner;
      const isInternalUser = userHasRole('CRS') || userHasRole('EPC');
      const statusId = orderDetails.statusId;
      orderDetails.status = getOrderStatusName(statuses, orderDetails, extOrderDetails);
      const formData = await getRelocationFormData(orderDetails, extOrderDetails);
      orderPrintViewCollection.add({
        orderId,
        allLineItems,
        orderDetails,
        extOrderDetails,
        internalPOCs,
        order: formData.order,
        formattedSchedules: formData.formattedSchedules,
        isCRSOwner,
        isEPCOwner,
        isInternalUser,
        statusId,
        isRelocation
      });
    })
  );

  return orderPrintViewCollection;
}

async function getRelocationFormData(orderDetails) {
  const [order, schedules] = await Promise.all([
    GetOFMFDRelocationByOrderId(orderDetails.orderId),
    GetDeliveryScheduleInfoForMFDRelocationForm(orderDetails)
  ]);
  const formattedSchedules = formatRelocationSchedules(schedules[0]);
  if (orderDetails.submittedOn) orderDetails.submittedOn = formatDisplayDate(orderDetails.submittedOn);
  if (order.relocationDate) order.relocationDate = formatDisplayDate(order.relocationDate);
  return { order, formattedSchedules };
}

function formatRelocationSchedules(schedules) {
  const deliverySchedules = {
    fromDevices: formatRelocationFromSchedule(schedules),
    toDevices: formatRelocationToSchedule(schedules),
    equipmentName: schedules?.deviceModel,
    equipmentMake: schedules?.deviceMake,
    serialNumber: schedules?.serialNumber
  };
  return deliverySchedules;
}

function formatRelocationFromSchedule(schedule) {
  const fromDevice = {};

  fromDevice.deliveryPOCName = schedule.fromDeliveryPOCName;
  fromDevice.deliveryPOCFirstName = schedule.fromDeliveryPOC?.deliveryPOCFirstName;
  fromDevice.deliveryPOCLastName = schedule.fromDeliveryPOC?.deliveryPOCLastName;
  fromDevice.deliveryPOCEmail = schedule.fromDeliveryPOC?.deliveryPOCEmail;
  fromDevice.deliveryPOCPhone = schedule.fromDeliveryPOC?.deliveryPOCPhone;

  fromDevice.itPOCName = schedule.fromITPOCName;
  fromDevice.itPOCFirstName = schedule.fromDeliveryPOC?.itPOCFirstName;
  fromDevice.itPOCLastName = schedule.fromDeliveryPOC?.itPOCLastName;
  fromDevice.itPOCEmail = schedule.fromITPOC?.itPOCEmail;
  fromDevice.itPOCPhone = schedule.fromITPOC?.itPOCPhone;

  fromDevice.organization = schedule.fromOrgUnit;
  fromDevice.base = schedule.fromBase;

  fromDevice.address = schedule.fromAddress;

  return fromDevice;
}

function formatRelocationToSchedule(schedule) {
  const toDevice = {};

  toDevice.deliveryPOCName = schedule.toDeliveryPOCName;
  toDevice.deliveryPOCFirstName = schedule.toDeliveryPOC?.deliveryPOCFirstName;
  toDevice.deliveryPOCLastName = schedule.toDeliveryPOC?.deliveryPOCLastName;
  toDevice.deliveryPOCEmail = schedule.toDeliveryPOC?.deliveryPOCEmail;
  toDevice.deliveryPOCPhone = schedule.toDeliveryPOC?.deliveryPOCPhone;

  toDevice.itPOCName = schedule.toITPOCName;
  toDevice.itPOCFirstName = schedule.toITPOC?.itPOCFirstName;
  toDevice.itPOCLastName = schedule.toITPOC?.itPOCLastName;
  toDevice.itPOCEmail = schedule.toITPOC?.itPOCEmail;
  toDevice.itPOCPhone = schedule.toITPOC?.itPOCPhone;

  toDevice.organization = schedule.toOrgUnit;
  toDevice.base = schedule.toBase;

  toDevice.address = schedule.toAddress;

  return toDevice;
}

function getOrderStatusName(statuses, orderDetails, extOrderDetails) {
  let status;
  if (EXTERNAL) {
    _.each(statuses, (s) => {
      if (s.id === extOrderDetails.statusId) status = s.externalOrderStatus;
    });
  } else {
    _.each(statuses, (s) => {
      if (s.id === orderDetails.statusId) status = s.internalOrderStatus;
    });
  }
  return status;
}
